import { type FormEvent, useEffect, useRef, useState } from 'react';
import { ArrowRight, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { useDiagnosticStore } from '../../../stores/diagnostic';
import { apiFetch, ApiError } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { PROFILES, type ProfileId, formatSiret, Eyebrow } from './types';

interface Step2SiretProps {
  profile: ProfileId;
  onNext: () => void;
}

export function Step2Siret({ profile, onNext }: Step2SiretProps) {
  const store = useDiagnosticStore();
  const [raw, setRaw] = useState('');
  const digits = raw.replace(/\D/g, '');
  const profileObj = PROFILES.find((p) => p.id === profile);
  const siretInputRef = useRef<HTMLInputElement>(null);

  // Focus programmatique au mount, équivalent fonctionnel d'autoFocus mais
  // sans son problème d'accessibilité (autoFocus peut surprendre un lecteur
  // d'écran et interrompre la lecture du contexte).
  useEffect(() => {
    const t = setTimeout(() => siretInputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (digits.length !== 14) {
      store.setError('Le SIRET doit comporter exactement 14 chiffres.');
      return;
    }
    store.setError(null);
    store.setLoading(true);
    try {
      const data = await apiFetch<{
        siret: string;
        name: string;
        naf: string | null;
        segment: string;
        questions: never[];
      }>('/api/diagnostic/siret', { json: { siret: digits } });
      // Hydrate le store AVANT de naviguer : Step3Questions doit monter
      // avec les questions déjà disponibles (sinon flash d'étape vide).
      store.setSiretContext({
        siret: data.siret,
        name: data.name,
        segment: data.segment,
        naf: data.naf,
        questions: data.questions,
      });
      onNext();
    } catch (err) {
      store.setError(
        err instanceof ApiError
          ? err.message
          : 'Impossible de trouver ce SIRET. Vérifiez le numéro et réessayez.',
      );
    } finally {
      store.setLoading(false);
    }
  }

  return (
    <div>
      <Eyebrow>Étape 2 sur 5</Eyebrow>
      <h3 className="mt-2 h-section">
        {profile === 'artisan' ? 'Identifiez votre entreprise RGE' : 'Identifiez votre entreprise'}
      </h3>
      <p className="mt-1.5 text-[13px] leading-relaxed text-stone-500">
        Nous interrogeons le registre officiel des entreprises pour pré-remplir votre secteur et personnaliser l'estimation.
      </p>

      <form onSubmit={onSubmit} className="mt-6 space-y-4">
        <div>
          <Label htmlFor="siret" className="text-[13px] font-semibold">
            Numéro SIRET
          </Label>
          <div className="relative mt-1.5">
            <Input
              id="siret"
              ref={siretInputRef}
              inputMode="numeric"
              maxLength={19}
              required
              value={formatSiret(digits)}
              onChange={(e) => {
                const next = e.target.value.replace(/\D/g, '');
                if (next.length <= 14) setRaw(next);
              }}
              placeholder="000 000 000 00000"
              className="h-12 rounded-xl border-2 border-stone-200 pr-14 font-mono text-lg tracking-widest focus:border-brand-400"
            />
            {digits.length > 0 && (
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[11px] font-semibold tabular-nums text-stone-400">
                {digits.length}/14
              </span>
            )}
          </div>
          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-stone-400">
            <ShieldCheck className="h-3 w-3 shrink-0 text-brand-500" />
            Via le registre officiel des entreprises — aucune donnée stockée hors UE
          </div>
        </div>

        {profileObj && (
          <div className="flex items-center gap-2 rounded-lg border border-stone-100 bg-stone-50 px-3 py-2.5">
            <div className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-lg', profileObj.iconBg)}>
              <profileObj.icon className="h-3.5 w-3.5" strokeWidth={1.7} />
            </div>
            <span className="text-[12px] text-stone-600">
              Profil sélectionné :{' '}
              <strong className="font-semibold text-ink">{profileObj.label}</strong>
            </span>
          </div>
        )}

        <Button
          type="submit"
          variant="brand"
          size="xl"
          disabled={store.loading || digits.length !== 14}
          className="w-full gap-2"
        >
          {store.loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Vérification en cours…
            </>
          ) : (
            <>
              Identifier mon entreprise
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
      </form>
    </div>
  );
}
